import { useState } from 'react';
import {
	add,
	DocumentClass,
	DocumentClassSchema,
} from '@/api/doc/document-class';
import { Button } from '@/components/ui/button';
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from '@/components/ui/sheet';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CirclePlus } from 'lucide-react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'sonner';

export function AddDocumentClass() {
	const [isSheetOpen, setIsSheetOpen] = useState(false);
	const queryClient = useQueryClient();

	const form = useForm<DocumentClass>({
		resolver: zodResolver(DocumentClassSchema),
		defaultValues: {
			id: null,
			name: '',
			slug: null,
			deletedOn: null,
		},
		mode: 'all',
	});
	const { errors, isValid, isSubmitting } = form.formState;

	const { mutate } = useMutation({
		mutationFn: (insertedDocument: DocumentClass) => add(insertedDocument),
		onSuccess: () => {
			toast.success(`Success! The document class has been successfully added`, {
				dismissible: true,
			});
			setIsSheetOpen(false);
			queryClient.invalidateQueries({ queryKey: ['document-classes'] });
		},
		onError: () => {
			toast.error(`Error! The document class could not be added`, {
				dismissible: true,
			});
		},
	});
	const onSubmit: SubmitHandler<DocumentClass> = (values: DocumentClass) =>
		mutate(values);

	form.watch();
	console.log({ errors, isValid, isSubmitting });
	return (
		<Sheet open={isSheetOpen} onOpenChange={(open) => setIsSheetOpen(open)}>
			<SheetTrigger asChild>
				<Button>
					<CirclePlus />
					Add Document Class
				</Button>
			</SheetTrigger>
			<SheetContent className="overflow-scroll">
				<SheetHeader>
					<SheetTitle>Add Document Class</SheetTitle>
				</SheetHeader>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
						<FormField
							control={form.control}
							name="name"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Document Class Name</FormLabel>
									<FormControl>
										<Input
											{...field}
											placeholder="Document class name"
											autoComplete="off"
										/>
									</FormControl>
									<FormDescription>Name the document class</FormDescription>
									<FormMessage />
								</FormItem>
							)}
						/>

						<Button type="submit" disabled={!isValid || isSubmitting}>
							{isSubmitting ? 'Adding...' : 'Add'}
						</Button>
					</form>
				</Form>
			</SheetContent>
		</Sheet>
	);
}
